import { cloneElement, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Page, Group, Layout, Button, ExcelUpload } from "@/comn/components";
import {
    useForm,
    useToast,
    useFetch,
    useModal,
    useAuth,
    useStore,
    useGrid,
    usePopup,
    useResource,
    useExcel,
    usePage,
} from "@/comn/hooks";
import { APIS_ADD_ITM } from "@/tz/go/egaz/osci/lpco/comn/services/OsciLpcoComnAppOfcrAddItmService";

export const APP_RGST_OFCR_ADD_ITM = (props: any) => {
    // const pgeUid = "APP_DTL_ADD_ITM"; // Page Unique identifier !== 화면 고유 식별자 ==!
    const { pgeUid, menuId } = usePage();
    const { t } = useTranslation(); // Translation Hook !== 언어 변화 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!

    /** #add-item# additional item */
    const refAddItmList = useRef<any[]>([]); /** addtional item info list */
    const [stateAddItmSchemas, setStateAddItmSchemas] = useState<any[] | undefined>([]);
    const refTemp = useRef<any[]>([]);

    const form = props.form;

    //const form: { [name: string]: FormType } = props.state.form;
    // {
    //     appRgstAddItm: useForm({
    //         defaultSchema: SF_APP_DTL_ADD_ITM,
    //     }),
    // };

    // Default read-only; editable only when parent sets param.edit === true !== 기본 조회, param.edit === true 일 때만 편집 ==!
    const canEdit = props.param?.edit === true;

    // !== 2026-04-22 KNH: 추가 필드의 변경 여부를 판단할 함수 ==!
    const onOfcrAddItmChange = props.onOfcrAddItmChange as ((change: boolean) => void) | undefined;

    // !== 2026-04-22 KNH: 탭별 저장 함수 등록 ==!
    const onRegisterTabSave = props.onRegisterTabSave as
        | ((tabCd: string, fn?: () => Promise<boolean>) => void)
        | undefined;

    // !== 2026-04-22 KNH: 탭별 필수값 검증 함수 등록 ==!
    const onRegisterTabValid = props.onRegisterTabValid as
        | ((tabCd: string, fn?: () => Promise<boolean>) => void)
        | undefined;

    // watch() 호출로 값 변경 시 리렌더 유도
    const watchedValues = form?.appDtlOfcrAddItm?.watch ? form.appDtlOfcrAddItm.watch() : undefined;

    useResource({
        defaultSchema: [], //{ area: "comnCd", comnCd: "" }
    });

    const fetch = {
        /** #add-item# additional item */
        getAdtnlItmValueList: useFetch({
            api: (appId, data) => {
                // console.log("getAdtnlItmValueList data", data);
                return APIS_ADD_ITM.getAdtnlItmValueList(appId, data);
            },
            onSuccess: (data) => {
                // !== 2026-04-22 KNH: 최초 DB에서 가져온 값을 저장 해 둠 ==!
                refAddItmList.current = data.adtnlItmList?.content || [];
                form.appDtlOfcrAddItm?.setValue("orginAdtnlItmList", [...refAddItmList.current]);
                const schemaEdit = props.param?.edit === true;
                setStateAddItmSchemas(
                    APIS_ADD_ITM.getAdtnlItmSchemas(
                        form.appDtlOfcrAddItm,
                        data.adtnlItmList?.content || [],
                        schemaEdit,
                    ),
                );
                if (!schemaEdit) {
                    form.appDtlOfcrAddItm?.setEditable(false);
                }
                // !== 로드 직후는 변경되지 않았음 ==!
                if (schemaEdit) {
                    onOfcrAddItmChange?.(false);
                }
            },
            onError: () => {},
            showToast: false,
        }),
        saveAdtnlItmValueList: useFetch({
            api: (data) => {
                return APIS_ADD_ITM.saveAdtnlItmValueList(props.param?.appId, data);
            },
            // onSuccess: () => {
            //     handler.handleSaveSuccess();
            // },
            // onError: () => {},
        }),
    };

    const handler = {
        // !== 2026-04-22 KNH: 추가 필드의 변경 여부를 판단 ==!
        isChangeByOrigin: useCallback(() => {
            // !== 2026-04-22 KNH: 현재 설정된 값 ==!
            const allValues = form?.appDtlOfcrAddItm?.getValues?.() || {};
            const originList = allValues?.orginAdtnlItmList || [];

            if (!originList || originList.length === 0) return false;

            for (let i = 0; i < originList.length; i++) {
                const row = originList[i];
                const fieldName = row?.adtnlItmCdNo;

                if (!fieldName) continue;

                const originVal = String(row?.adtnlItmCn ?? "");
                const currentVal = String(allValues?.[fieldName] ?? "");

                if (originVal !== currentVal) {
                    return true;
                }
            }
            return false;
        }, [form]),
        // !== 2026-04-22 KNH: 저장 성공 시 추가 필드의 기본 정보와 변경 여부를 초기화 ==!
        handleSaveSuccess: useCallback(() => {
            const allValues = form?.appDtlOfcrAddItm?.getValues?.() || {};
            const originList = [...(allValues?.orginAdtnlItmList || [])];
            const nextOrigin = originList.map((row: any) => {
                const key = row?.adtnlItmCdNo;
                return {
                    ...row,
                    adtnlItmCn: key ? allValues[key] : row?.adtnlItmCn,
                };
            });
            form.appDtlOfcrAddItm?.setValue("orginAdtnlItmList", nextOrigin);
            onOfcrAddItmChange?.(false);
            // Reload so rows just inserted carry their adtnlItmId; without it the next save inserts again
            // !== 방금 INSERT된 행의 adtnlItmId를 받아오기 위해 재조회한다. 없으면 다음 저장이 또 INSERT된다 ==!
            const appId = props.param?.appId;
            if (appId && props.param?.appClsfCd !== "X") {
                fetch.getAdtnlItmValueList.fetch(appId, {
                    additmCpntCd: props.area,
                    appCdNo: props.param?.appCdNo,
                    itmNo: props.itemNo,
                    appDgr: props.param?.appDgr,
                    appStatCd: props.param?.appStatCd,
                    prcsRoleTpCd: props.param?.prcsRoleTpCd,
                });
            }
        }, [form, onOfcrAddItmChange, props.param, props.area, props.itemNo]),
        // !== 2026-04-22 KNH: 추가 필드의 저장 ==!
        saveOfcrAddItm: useCallback(async (): Promise<boolean> => {
            // Validate before the save confirm modal; invalid fields show their own inline message
            // !== 저장 확인 모달 전에 검증한다. 위반 항목은 각자 인라인 메시지를 표시한다 ==!
            const valid = await handler.validOfcrAddItm();
            if (!valid) {
                return false;
            }

            return new Promise<boolean>((resolve) => {
                modal.openModal({
                    content: "msg.osc.00058", // Do you want to save? !== 저장하시겠습니까? ==!
                    onConfirm: () => {
                        void (async () => {
                            const values = form?.appDtlOfcrAddItm?.getValues?.() || {};
                            const originList = [...(values?.orginAdtnlItmList || [])];
                            // console.log("saveOfcrAddItm originList", originList);
                            // const data = {
                            //     appId: props.param?.appId,
                            //     additmCpntCd: props.area, // GEN/TRN
                            //     adtnlItmList: APIS_ADD_ITM.getSaveAddItmList(values, originList),
                            // };
                            const data = APIS_ADD_ITM.getSaveAddItmList(values, originList, props.itemNo);

                            // console.log("data", data);
                            // fetch.saveAdtnlItmValueList.fetch(data);

                            const result = await fetch.saveAdtnlItmValueList.fetch(data);

                            if (result == undefined) {
                                // console.log("saveOfcrAddItm undefined error");
                                resolve(false);
                                return;
                            }

                            if (result?.isAxiosError || (result?.response && result?.response?.status >= 400)) {
                                // console.log("saveOfcrAddItm error", result);
                                resolve(false);
                                return;
                            }

                            handler.handleSaveSuccess();

                            resolve(true);
                        })();
                    },
                    onCancel: () => {
                        // console.log("onCancel");
                        resolve(false);
                    },
                });
            });
        }, [form, props.param, props.area, modal]),
        // !== 2026-04-22 KNH: 추가 필드의 필수값 검증 ==!
        validOfcrAddItm: useCallback(async (): Promise<boolean> => {
            // 추가항목이 없으면 검증할 것도 없으니 통과
            if (!stateAddItmSchemas || stateAddItmSchemas.length === 0) {
                return true;
            }

            const valid = await form.appDtlOfcrAddItm.validate();
            return !!valid;
        }, [form, props.param, props.area, modal]),
    };

    // 값이 바뀔 때마다 원본 대비 dirty 계산
    useEffect(() => {
        if (!canEdit || !onOfcrAddItmChange) return;
        if (!stateAddItmSchemas || stateAddItmSchemas.length === 0) return;

        const change = handler.isChangeByOrigin();
        onOfcrAddItmChange(change);
    }, [canEdit, watchedValues, stateAddItmSchemas, handler.isChangeByOrigin, onOfcrAddItmChange]);

    useEffect(() => {
        if (!canEdit || !onRegisterTabSave) return;
        onRegisterTabSave(props.area, async () => {
            const result = await handler.saveOfcrAddItm();
            return result;
        });
    }, [canEdit, onRegisterTabSave, props.area, handler.saveOfcrAddItm]);

    // 부모에 "이 탭의 valid 함수" 등록/해제
    useEffect(() => {
        if (!canEdit || !onRegisterTabValid) return;
        onRegisterTabValid(props.area, async () => {
            const result = await handler.validOfcrAddItm();
            return result;
        });
    }, [canEdit, onRegisterTabValid, props.area, handler.validOfcrAddItm]);

    useEffect(() => {
        const appCdNo = props.param?.appCdNo;
        const appId = props.param?.appId;
        const appClsfCd = props.param?.appClsfCd;
        const areaCd = props.area;
        const itemNo = props.itemNo;
        const appDgr = props.param?.appDgr;
        const appStatCd = props.param?.appStatCd;
        if (!appId || appClsfCd === "X") return;
        const paramData = {
            additmCpntCd: areaCd,
            appCdNo: appCdNo,
            itmNo: itemNo,
            appDgr: appDgr,
            appStatCd: appStatCd,
            prcsRoleTpCd: props.param?.prcsRoleTpCd,
        };
        fetch.getAdtnlItmValueList.fetch(appId, paramData);
    }, [
        props.param?.appId,
        props.param?.appCdNo,
        props.param?.appClsfCd,
        props.param?.appDgr,
        props.param?.appStatCd,
        props.param?.prcsRoleTpCd,
        props.area,
        props.itemNo,
    ]);

    return (
        <>
            {stateAddItmSchemas && stateAddItmSchemas.length > 0 && (
                <Layout direction="col">
                    <Layout>
                        <Layout.Left>
                            <Group.Title title={"T_ADTNL_INFO_LPCO"} titleSize={1}></Group.Title>
                        </Layout.Left>
                    </Layout>
                    <Group.Section>
                        {stateAddItmSchemas.map((elem, idx) => {
                            let rightButton = null;
                            if (refAddItmList.current[idx].inpTpCd == "CP") {
                                rightButton = APIS_ADD_ITM.getFormRightButtonCP(
                                    elem.inputName,
                                    elem.comnCd,
                                    form.appDtlOfcrAddItm,
                                    modal,
                                );
                            } else if (refAddItmList.current[idx].inpTpCd == "SP") {
                                rightButton = APIS_ADD_ITM.getFormRightButtonPP(
                                    elem.inputName,
                                    refAddItmList.current[idx].adtnlItmCdNo,
                                    form.appDtlOfcrAddItm,
                                    modal,
                                );
                            }

                            if (refAddItmList.current[idx].inpTpCd != "CP") {
                                refTemp.current.push(
                                    <Group.Control
                                        key={elem.inputName}
                                        {...form.appDtlOfcrAddItm.schema[elem.inputName]}
                                        rightButton={rightButton}
                                        edit={canEdit && refAddItmList.current[idx].editYn !== "N"}
                                    />,
                                );
                            } else {
                                refTemp.current.push(
                                    cloneElement(
                                        APIS_ADD_ITM.getFormComponentComnCd(
                                            elem,
                                            form.appDtlOfcrAddItm,
                                            elem.inputName,
                                            rightButton,
                                            canEdit && refAddItmList.current[idx].editYn !== "N",
                                        ),
                                        { key: elem.inputName },
                                    ),
                                );
                            }

                            if (idx % 2 == 1 || idx == stateAddItmSchemas.length - 1) {
                                let temp = [...refTemp.current];
                                refTemp.current = [];
                                return <Group.Row key={`row-${idx}`}>{temp}</Group.Row>;
                            }
                            return null;
                        })}
                    </Group.Section>
                </Layout>
            )}
        </>
    );
};
